import React, { useState, useEffect, useRef } from 'react';
import { arrayOf, string } from 'prop-types';
import classNames from 'classnames';

import { injectIntl, intlShape } from '../../util/reactIntl';
import { propTypes } from '../../util/types';
import { fetchCustomSlugByUserId } from '../../util/api';

import { Avatar, ReviewRating, UserDisplayName } from '../../components';

import css from './Reviews.module.css';

const Review = props => {
  const { review, intl, customSlug } = props;

  const date = review.attributes.createdAt;
  const dateString = intl.formatDate(date, { month: 'long', year: 'numeric' });

  return (
    <div className={css.review}>
      <Avatar className={css.avatar} user={review.author} customSlug={customSlug} />
      <div>
        <ReviewRating
          rating={review.attributes.rating}
          className={css.mobileReviewRating}
          reviewStarClassName={css.reviewRatingStar}
        />
        <div className={css.reviewContentWrapper}>
          <p className={css.reviewContent}>{review.attributes.content}</p>
        </div>
        <p className={css.reviewInfo}>
          <UserDisplayName user={review.author} intl={intl} />
          <span className={css.separator}>•</span>
          {dateString}
          <span className={css.desktopSeparator}>•</span>
          <span className={css.desktopReviewRatingWrapper}>
            <ReviewRating
              rating={review.attributes.rating}
              className={css.desktopReviewRating}
              reviewStarClassName={css.reviewRatingStar}
            />
          </span>
        </p>
      </div>
    </div>
  );
};

Review.propTypes = {
  review: propTypes.review.isRequired,
  intl: intlShape.isRequired,
};

const ReviewsComponent = props => {
  const { className, rootClassName, reviews, intl, isReviewsOfCustomer = false } = props;

  const [customSlugs, setCustomSlugs] = useState({});
  const requestedIdsRef = useRef(new Set());

  const classes = classNames(rootClassName || css.root, className);

  useEffect(() => {
    const fetchCustomSlugs = async () => {
      const newSlugs = {};

      for (const review of reviews) {
        const userId = review.author.id.uuid;

        if (customSlugs.hasOwnProperty(userId) || requestedIdsRef.current.has(userId)) {
          continue;
        }

        requestedIdsRef.current.add(userId);

        try {
          const { slug = '' } = await fetchCustomSlugByUserId(userId);
          newSlugs[userId] = slug;
        } catch (e) {
          console.error(e);
          newSlugs[userId] = '';
        }
      }

      if (Object.keys(newSlugs).length > 0) {
        setCustomSlugs(prev => ({ ...prev, ...newSlugs }));
      }
    };

    if (isReviewsOfCustomer && reviews.length > 0) {
      fetchCustomSlugs();
    }
  }, [isReviewsOfCustomer, reviews, customSlugs]);

  return (
    <ul className={classes}>
      {reviews.map(r => {
        return (
          <li key={`Review_${r.id.uuid}`} className={css.reviewItem}>
            <Review review={r} intl={intl} customSlug={customSlugs[r.author.id.uuid]} />
          </li>
        );
      })}
    </ul>
  );
};

ReviewsComponent.defaultProps = {
  className: null,
  rootClassName: null,
  reviews: [],
};

ReviewsComponent.propTypes = {
  className: string,
  rootCalssName: string,
  reviews: arrayOf(propTypes.review),

  // from injectIntl
  intl: intlShape.isRequired,
};

const Reviews = injectIntl(ReviewsComponent);

export default Reviews;
